import Layout from 'components/layout';

import React, { FC } from 'react';

import { getI18nData, isDefined } from '@lucid/utils';
import { graphql } from 'gatsby';
import { Box, Container, FeatureList, Quote, Heading, SimpleCta, Image } from '@lucid/components-react';
import {
  Drupal_FieldNodeSunriseFieldHeaderCta,
  Drupal_NodeSunrise,
  Drupal_ParagraphCtaButton,
  Drupal_ParagraphFullCard,
  Drupal_ParagraphHeroSection,
  Drupal_ParagraphPaperSection,
  Drupal_ParagraphQuote,
} from 'generated/drupal-graphql';
import { mqMax } from '@lucid/theme';

import CardTiles from 'components/card-tiles/card-tiles';
import Hero from 'components/hero/hero';
import SignUpCta from 'components/sign-up-cta/sign-up-cta';
import { Template, LucidTheme } from 'types/types';
import { getEntityFields } from 'utilities/getEntityFields';

const SunrisePage: FC<Template.PageProps> = ({ data, pageContext, location }) => {
  const { drupal, i18nData } = data;
  const node = drupal.nodeById as Drupal_NodeSunrise;
  const { entityMetatags, entitySchemaMetatags, entityTranslations } = getEntityFields(node);
  const { langPrefix, pageType, nid, i18nKey, shouldHideSsr } = pageContext;

  const i18n = getI18nData(i18nData, i18nKey);

  const headerSection = node.fieldHeaderSection
    ? (node.fieldHeaderSection.entity as Drupal_ParagraphHeroSection)
    : null;

  const headerCtas = (node.fieldHeaderCta as Drupal_FieldNodeSunriseFieldHeaderCta[]).map(
    ({ entity }) => entity as Drupal_ParagraphCtaButton,
  );

  const quote = node.fieldQuoteBundle ? (node.fieldQuoteBundle.entity as Drupal_ParagraphQuote) : null;

  const paperParagraphs = node
    .fieldPaperParagraph!.filter(isDefined)
    .map(({ entity }) => entity as Drupal_ParagraphPaperSection);

  const cards = node.fieldHowToSection!.filter(isDefined).map(({ entity }) => entity as Drupal_ParagraphFullCard);

  return (
    <Layout
      entityTranslations={entityTranslations}
      hasFooter={node.fieldHasFooter}
      hasNavbar={node.fieldHasNavbar}
      langPrefix={langPrefix}
      location={location}
      metatags={entityMetatags}
      nid={nid}
      pageType={pageType}
      schema={entitySchemaMetatags}
      shouldHideSsr={shouldHideSsr}
    >
      <Box
        css={(theme: LucidTheme) => [
          {
            position: 'relative',
            zIndex: theme.zIndices[1],
          },
          theme.shared.backgroundWithGrayPattern,
        ]}
      >
        {headerSection && (
          <Hero
            hasBackgroundImage
            backgroundImage={headerSection.fieldImage!}
            breadCrumbPage={node.fieldTitle!}
            buttons={headerCtas}
            header={headerSection.fieldHeader!}
            pageType={pageType}
            subheader={headerSection.fieldSubheader! || headerSection.fieldLongSubheader!}
            videoLink={node.fieldHeaderVideoLink!}
          />
        )}
        <Container>
          {paperParagraphs && <FeatureList list={paperParagraphs} />}
          {quote && quote.fieldQuote && <Quote bottomAttribution={quote.fieldAttribution} quote={quote.fieldQuote} />}
        </Container>
        {node.fieldFeaturedImage && (
          <Container>
            <Box
              css={(theme: LucidTheme) => ({
                borderRadius: theme.radii.card,
                boxShadow: theme.shadows[20],
              })}
              my={8}
            >
              <Image
                imageAlt=""
                imageSrc="https://d2slcw3kip6qmk.cloudfront.net/marketing/pages/chart/consideration/basic-images/browser-bar@2x.png"
                loading="lazy"
              />
              <Image imageAlt="" imageSrc={node.fieldFeaturedImage} loading="lazy" />
            </Box>
          </Container>
        )}
        {cards.length > 0 && (
          <Box
            css={(theme: LucidTheme) => ({
              backgroundColor: theme.colors.coolGrey[100],
            })}
            id="templates"
          >
            <Container>
              <Heading as="h2" color="white" pt={{ default: 5 }} variant="headlineXL">
                {node.fieldFeatureTitle || i18n('mktg-footer-resources-templates')}
              </Heading>
              <CardTiles tiles={cards} />
            </Container>
          </Box>
        )}
        {node.fieldHasFooterEmailOnly && (
          <SimpleCta
            css={(theme: LucidTheme) => ({
              backgroundColor: theme.colors.coolGrey[100],
            })}
            heading={i18n('mktg_ct_consideration_footer_header')}
          >
            <SignUpCta css={{ [mqMax.mobile]: { width: '100%' } }} />
          </SimpleCta>
        )}
      </Box>
    </Layout>
  );
};

export default SunrisePage;

export const query = graphql`
  query ($nid: String!, $LanguageId: Drupal_LanguageId!, $i18nKey: String!) {
    drupal {
      nodeById(id: $nid, language: $LanguageId) {
        ... on Drupal_NodeSunrise {
          ...entityFields
          fieldTitle
          fieldHeaderSection {
            entity {
              ... on Drupal_ParagraphHeroSection {
                fieldHeader
                fieldSubheader
                fieldLongSubheader
                fieldImage
              }
            }
          }
          fieldHeaderCta {
            entity {
              ... on Drupal_ParagraphCtaButton {
                fieldCtaData {
                  title
                  url {
                    path
                  }
                }
              }
            }
          }
          fieldPaperParagraph {
            entity {
              ... on Drupal_ParagraphPaperSection {
                fieldImage
                fieldHeader
                fieldDescriptionWysiwyg {
                  processed
                }
                fieldCta {
                  url {
                    path
                  }
                  title
                }
              }
            }
          }
          fieldQuoteBundle {
            entity {
              ... on Drupal_ParagraphQuote {
                fieldQuote
                fieldAttribution
              }
            }
          }
          fieldHowToSection {
            entity {
              ... on Drupal_ParagraphFullCard {
                fieldImage
                fieldCtaHeading
                fieldLink
                fieldCardSubheader {
                  processed
                }
              }
            }
          }
          fieldHasNavbar
          fieldHasFooter
          fieldHasFooterEmailOnly
          fieldHeaderVideoLink
          fieldUseEmailOnly
          fieldFeaturedImage
          fieldFeatureTitle
        }
      }
    }
    i18nData: allFile(filter: { sourceInstanceName: { eq: "i18n" }, name: { in: [$i18nKey, "en"] } }) {
      edges {
        node {
          name
          childSrcJson {
            mktg_ct_solutions_footer_cta_primary
            mktg_ct_solutions_footer_or_call
            mktg_ct_consideration_footer_header
            mktg_ct_all_request_a_demo
            mktg_ct_all_get_a_quote
            mktg_footer_resources_templates
          }
        }
      }
    }
  }
`;
